import * as Haptics from 'expo-haptics';
import FlashlightOffIcon from '@assets/icons/flashlight-off.svg';
import FlashlightOnIcon from '@assets/icons/flashlight-on.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BORDER_RADIUS_LARGE, TYPOGRAPHY } from '@/styles';
import { Button } from '@/components/button';
import {
  Camera,
  Code,
  CodeScannerFrame,
  useCameraDevice,
  useCodeScanner,
} from '@/react-native-vision-camera';
import { CAMERA_FILTER, CAMERA_POSITION } from '@/utils/camera';
import { CornerArcs } from '@/components/corner-arcs';
import { createStyles } from '@/contexts/theme-context';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import {
  LayoutSize,
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { Platform, Text, useWindowDimensions, View } from 'react-native';
import { runOnJS } from 'react-native-reanimated';
import { useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUserSettingsContext } from '@/contexts/user-context';

const HIT_SLOP = {
  bottom: 16,
  left: 16,
  right: 16,
  top: 16,
};

const SCAN_TIMEOUT = 5000;
const SCAN_ACCESSORY_BUTTON_SIZE = 36;

export const SCAN_BUTTON_SIZE = 72;
export const SCAN_BUTTON_SIZE_LARGE = 58;
export const SCAN_BUTTON_ROW_PADDING = 24;
export const NAVIGATION_HEADER_HEIGHT = 60;
export const CAMERA_SCANNER_HEIGHT_MODIFIERS: Record<LayoutSize, number> = {
  LARGE: 0.4,
  MEDIUM: 0.5,
  SMALL: 0.54,
  SMALL_TABLET: 0.5,
};

export interface Props {
  canUseCamera: boolean;
  errorText?: string | null;
  onBarcodeScanned: (result: Code) => void;
  onClear: () => void;
}
const OVERLAY_X_WIDTH_SMALL = 16;
const OVERLAY_X_WIDTH_LARGE = 48;

export const BarcodeScanner: React.FC<Props> = ({
  canUseCamera,
  errorText,
  onBarcodeScanned,
  onClear,
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState<Code | null>(null);
  const [boxHeight, setBoxHeight] = useState(0);
  const [boxWidth, setBoxWidth] = useState(0);
  // The viewfinder box's position relative to its row (centerOverlay) and
  // that row's own header offset, both measured via onLayout rather than
  // assumed from style constants, since things like safe area insets can
  // shift the actual rendered position away from what those constants predict.
  const [boxOffsetX, setBoxOffsetX] = useState(0);
  const [boxOffsetY, setBoxOffsetY] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);

  const [styles, theme] = useStyles();
  const { top } = useSafeAreaInsets();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const { layoutSize, isSmallLayout } = useLayoutContext();
  const device = useCameraDevice(CAMERA_POSITION, CAMERA_FILTER);
  const isFocused = useIsFocused();
  const scannerHeaderHeight = NAVIGATION_HEADER_HEIGHT + top;
  const scannerContainerHeight = Math.ceil(
    windowHeight * CAMERA_SCANNER_HEIGHT_MODIFIERS[layoutSize],
  );

  const overlayXStyle = useSmallLayoutSizeValue(
    styles.overlayX,
    styles.overlayXLarge,
  );
  const scanningTextStyle = useSmallLayoutSizeValue(
    styles.scanTextPadding,
    styles.scanTextPaddingLarge,
  );
  const scanButtonStyle = useSmallLayoutSizeValue(
    styles.scanButton,
    styles.scanButtonLarge,
  );

  const { shouldFlashlightActivate, setShouldFlashlightActivate } =
    useUserSettingsContext();
  const camera = useRef<Camera>(null);

  // it's possible that onCodeScanned can be called multiple times before the
  // state update and re-render occurs. This will increment the quantity
  // multiple times per button click. Use a ref guard to ensure one click = one scan.
  const isProcessingScanRef = useRef(false);

  const canUseTorch = Boolean(device?.hasTorch || device?.hasFlash);

  // Ensure flashlight state is reset if the device doesn't support torch
  useEffect(() => {
    if (!canUseTorch && shouldFlashlightActivate) {
      setShouldFlashlightActivate(false);
    }
  }, [canUseTorch, shouldFlashlightActivate, setShouldFlashlightActivate]);

  useEffect(() => {
    if (isScanning) {
      const timeoutId = setTimeout(() => {
        setIsScanning(false);
      }, SCAN_TIMEOUT);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isScanning]);

  const scanResultText = useMemo(() => {
    if (errorText) {
      return scannedBarcode
        ? i18n.t('scan.no_results_for', { code: scannedBarcode.value })
        : errorText;
    }
    if (isScanning) {
      return i18n.t('scanning');
    } else if (scannedBarcode) {
      return i18n.t('scan.scan_result', { code: scannedBarcode.value });
    }
    return ' '; // Use space character to maintain line spacing
  }, [errorText, isScanning, scannedBarcode]);

  // Only the scanner preview area behind the visible viewfinder box is
  // meant to be scannable; react-native-vision-camera otherwise detects
  // codes anywhere in the full camera frame, including outside that box.
  //
  // `Code.frame` is reported in the camera sensor's native (unrotated,
  // landscape, home-button-right) coordinate space regardless of the
  // portrait interface orientation, not in the view's portrait dp space
  // that boxWidth/boxHeight/etc. are measured in — confirmed both from
  // vision-camera's native source (bounds are scaled by
  // device.activeFormat.videoDimensions, which is always landscape) and
  // on-device testing. Converting to portrait dp requires a 90° rotation.
  const isCodeInViewfinder = useCallback(
    (code: Code, frame: CodeScannerFrame) => {
      if (
        !code.frame ||
        !boxWidth ||
        !boxHeight ||
        !windowWidth ||
        !scannerContainerHeight
      ) {
        return true;
      }
      const sensorCenterX = code.frame.x + code.frame.width / 2;
      const sensorCenterY = code.frame.y + code.frame.height / 2;
      const portraitCenterX = windowWidth * (1 - sensorCenterY / frame.height);
      const portraitCenterY =
        scannerContainerHeight * (sensorCenterX / frame.width);

      const viewfinderLeft = boxOffsetX;
      const viewfinderTop = headerHeight + boxOffsetY;

      return (
        portraitCenterX >= viewfinderLeft &&
        portraitCenterX <= viewfinderLeft + boxWidth &&
        portraitCenterY >= viewfinderTop &&
        portraitCenterY <= viewfinderTop + boxHeight
      );
    },
    [
      boxOffsetX,
      headerHeight,
      boxOffsetY,
      boxWidth,
      boxHeight,
      windowWidth,
      scannerContainerHeight,
    ],
  );

  const codeScanner = useCodeScanner({
    codeTypes: [
      'code-128',
      'code-39',
      'code-93',
      'codabar',
      'ean-13',
      'ean-8',
      'itf',
      'upc-e',
      'upc-a',
    ],
    onCodeScanned: (codes: Code[], frame: CodeScannerFrame) => {
      if (isProcessingScanRef.current || !isScanning || !codes?.length) {
        return;
      }
      const code = codes.find((c) => isCodeInViewfinder(c, frame));
      if (!code) {
        return;
      }
      isProcessingScanRef.current = true;

      setIsScanning(false);
      // iOS prefers scanning UPC-A codes as EAN-13 which adds a leading 0
      // to the value so strip the leading 0 to normalize back to the UPC-A format
      if (
        Platform.OS === 'ios' &&
        code.type === 'ean-13' &&
        code.value?.startsWith('0')
      ) {
        code.value = code.value.slice(1);
      }
      setScannedBarcode(code);
      onBarcodeScanned(code);
    },
  });

  const focus = useCallback(async (point: { x: number; y: number }) => {
    try {
      await camera.current?.focus(point);
    } catch {
      // Do nothing
    }
  }, []);

  const gesture = Gesture.Tap().onEnd(({ x, y }) => {
    runOnJS(focus)({ x, y });
  });

  const flashLightButton = canUseTorch ? (
    <Button
      containerStyle={isSmallLayout ? styles.scanAccessoryButton : undefined}
      hitSlop={HIT_SLOP}
      Icon={shouldFlashlightActivate ? FlashlightOnIcon : FlashlightOffIcon}
      iconProps={{
        fill: shouldFlashlightActivate
          ? theme.sk.color.icon.primary
          : theme.sk.color.icon.ondark,
      }}
      onPress={() => setShouldFlashlightActivate(!shouldFlashlightActivate)}
      rounded
      size="small"
      style={[
        styles.button,
        shouldFlashlightActivate
          ? styles.flashlightActive
          : styles.flashlightInactive,
      ]}
      testID="flashlight-toggle"
      trackingData={{
        name: 'upc_flashlight_button',
        properties: {
          value: !shouldFlashlightActivate,
        },
      }}
    />
  ) : (
    // Render an empty View to maintain layout
    <View style={styles.scanAccessoryButton} />
  );

  const scanButton = (
    <Button
      containerStyle={isSmallLayout ? undefined : styles.scanButtonContainer}
      disabled={isScanning}
      onPress={() => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        onClear();
        setScannedBarcode(null);
        isProcessingScanRef.current = false;
        setIsScanning(true);
      }}
      rounded
      style={scanButtonStyle}
      testID="scan-button"
      trackingData={{
        name: 'catalog_scanner.scan_button',
      }}
    />
  );

  const cameraViewFinder = (
    <View pointerEvents="box-none" style={{ height: scannerContainerHeight }}>
      <View
        onLayout={(e) => {
          setHeaderHeight(Math.ceil(e.nativeEvent.layout.height));
        }}
        style={{
          backgroundColor: theme.colors.overlay,
          height: scannerHeaderHeight,
        }}
        testID="viewfinder-header"
      />
      <View pointerEvents="box-none" style={styles.centerOverlay}>
        <View style={overlayXStyle} />
        <View
          onLayout={(e) => {
            const { height, width, x, y } = e.nativeEvent.layout;
            setBoxHeight(Math.ceil(height));
            setBoxWidth(Math.ceil(width));
            setBoxOffsetX(Math.ceil(x));
            setBoxOffsetY(Math.ceil(y));
          }}
          pointerEvents="none"
          style={styles.content}
          testID="viewfinder-box"
        >
          {/* Content-fit box sizing isn't supported in React Native, so apply
          corner arcs to the camera viewfinder to mimic behavior */}
          <CornerArcs
            borderRadius={BORDER_RADIUS_LARGE}
            fill={theme.colors.overlay}
            height={boxHeight}
            width={boxWidth}
          />
        </View>
        {isSmallLayout ? (
          <View style={overlayXStyle} />
        ) : (
          <View style={styles.tabletButtonsWrapper}>
            {flashLightButton}
            {scanButton}
          </View>
        )}
      </View>
      <Text style={[TYPOGRAPHY.pSemiBold, styles.scanText, scanningTextStyle]}>
        {scanResultText}
      </Text>
      {isSmallLayout ? (
        <View style={styles.scanButtonRow}>
          <View style={styles.scanAccessoryButton} />
          {scanButton}
          {flashLightButton}
        </View>
      ) : null}
    </View>
  );

  return canUseCamera && device ? (
    <>
      <GestureDetector gesture={gesture}>
        <Camera
          codeScanner={codeScanner}
          device={device}
          enableZoomGesture
          isActive={isFocused}
          ref={camera}
          style={[styles.barcodeScanner, { height: scannerContainerHeight }]}
          testID="camera-view"
          torch={canUseTorch && shouldFlashlightActivate ? 'on' : 'off'}
        />
      </GestureDetector>
      {cameraViewFinder}
    </>
  ) : (
    // Use containing View for BottomSheet layout placement
    <View collapsable={false} testID="camera-placeholder">
      {cameraViewFinder}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  barcodeScanner: {
    flexDirection: 'column',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  button: {
    height: SCAN_ACCESSORY_BUTTON_SIZE,
    width: SCAN_ACCESSORY_BUTTON_SIZE,
  },
  centerOverlay: {
    backgroundColor: theme.colors.transparent,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    backgroundColor: theme.colors.transparent,
    flex: 1,
    overflow: 'hidden',
  },
  flashlightActive: {
    backgroundColor: theme.sk.color.bg.button.primary.disabled,
  },
  flashlightInactive: {
    backgroundColor: theme.sk.color.bg.dark.default,
  },
  overlayX: {
    backgroundColor: theme.colors.overlay,
    width: OVERLAY_X_WIDTH_SMALL,
  },
  overlayXLarge: {
    backgroundColor: theme.colors.overlay,
    width: OVERLAY_X_WIDTH_LARGE,
  },
  scanAccessoryButton: {
    alignItems: 'center',
    flex: 1,
  },
  scanButton: {
    backgroundColor: theme.sk.color.bg.primary.default,
    height: SCAN_BUTTON_SIZE,
    width: SCAN_BUTTON_SIZE,
  },
  scanButtonContainer: {
    borderColor: theme.sk.color.bg.primary.default,
    borderRadius: SCAN_BUTTON_SIZE_LARGE,
    borderWidth: 4,
    padding: 4,
  },
  scanButtonLarge: {
    backgroundColor: theme.sk.color.bg.primary.default,
    height: SCAN_BUTTON_SIZE_LARGE,
    width: SCAN_BUTTON_SIZE_LARGE,
  },
  scanButtonRow: {
    alignItems: 'center',
    backgroundColor: theme.colors.overlay,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    paddingBottom: SCAN_BUTTON_ROW_PADDING,
  },
  scanText: {
    backgroundColor: theme.colors.overlay,
    color: theme.sk.color.text.ondark,
    textAlign: 'center',
  },
  scanTextPadding: {
    paddingVertical: 8,
  },
  scanTextPaddingLarge: {
    paddingVertical: 48,
  },
  tabletButtonsWrapper: {
    alignItems: 'center',
    backgroundColor: theme.colors.overlay,
    gap: 48,
    justifyContent: 'flex-end',
    paddingHorizontal: 24,
  },
}));
